import { FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import { ROUTES } from '@/route/routes.constants';
import styles from './StartInspectionPage.module.scss';
import actionStyles from './styles/inspectionActionButtons.module.scss';
import { systemConfig } from '@/config/systemConfig';
import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { clivApiRequest } from '@/utils/apiClient';
import { Inspection } from '@/types/Inspection.type';
import { loadInspectionForInspectApp } from '@/utils/ensureInspectionHydrated';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { isFactoryVerificationEnabled } from '@/utils/factoryVerificationConfig.util';
import { useAppSelector } from '@/store/hooks';
import store from '@/store/store';
import { upsertInspection } from '@/store/slices/inspectionsSlice';
import { clearOpenInspectionLoadError, closeInspection } from '@/store/slices/openInspectionSlice';
import { useNetwork } from '@/hooks/useNetwork';
import { useSavedInspection } from '@/hooks/useSavedInspection';
import { ExistingOpenInspectionDialog } from '@/features/inspection-list/components/ExistingOpenInspectionDialog';
import { StartInspectionGpsVerification } from '@/features/inspection-list/components/StartInspectionGpsVerification';
import { useStartInspectionGpsVerification } from '@/features/inspection-list/hooks/useStartInspectionGpsVerification';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { ROLE } from '@/role.constant';
import { buildInspectionStartPut } from '@/utils/inspectionStartFlow.utils';
import { getInspectionStartPageDecision } from '@/utils/inspectionStart.utils';
import { applyAutoReusedEvidenceAndStart } from '@/utils/locationVerificationStart';

/**
 * Precheck page before opening an inspection.
 * Validates that the user can start it, then updates status and navigates to the inspection.
 */
export const StartInspectionPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inspectionDetails, setInspectionDetails] = useState<Inspection | null>(null);
  const [showOpenInspectionDialog, setShowOpenInspectionDialog] = useState(false);
  const conflictHandledRef = useRef(false);
  const autoOpenHandledRef = useRef(false);
  const startInFlightRef = useRef(false);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const startInspectionCheck = useAppSelector(
    (state: RootState) => state.appConfig[APP_CONFIG_KEY.startInspectionCheck]
  );
  const factoryVerificationEnabled = useAppSelector((state: RootState) =>
    isFactoryVerificationEnabled(state.appConfig)
  );
  const cachedInspection =
    useAppSelector((state: RootState) =>
      state.inspectionsData?.inspections.find((i: Inspection) => i.id === id)
    ) || null;
  const openInspection = useAppSelector((state: RootState) => state.openedInspection.inspection);
  const { hasRole } = useRoleAccess();
  const isAdministrator = hasRole(ROLE.ADMINISTRATOR);
  const isOnline = useNetwork();
  const { savedInspection, isLoading: isSavedInspectionLoading } = useSavedInspection(id);
  const gpsVerification = useStartInspectionGpsVerification({
    inspectionId: id,
    inspection: inspectionDetails,
    factoryVerificationEnabled,
    hasSavedInspection: Boolean(savedInspection),
    inspectorId: user?.id,
    isOnline,
    navigate,
  });
  const inspectionUrl = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.INSPECTIONS}/${id}`;

  useEffect(() => {
    if (conflictHandledRef.current) return;
    if (!id) return;
    if (openInspection?.id && openInspection.id !== id) {
      setShowOpenInspectionDialog(true);
      return;
    }
    if (isAdministrator) {
      navigate(ROUTES.INSPECTION_DETAILS.replace(':id', id), {
        state: { skipStartWorkFlow: true },
      });
    }
  }, [id, openInspection?.id, isAdministrator, navigate]);

  const gotoInspection = useCallback(
    (inspection: Inspection, inspectionId: string) => {
      store.dispatch(upsertInspection(inspection));
      navigate(ROUTES.INSPECTION_DETAILS.replace(':id', inspectionId), {
        state: { skipStartWorkFlow: true },
      });
    },
    [navigate]
  );

  useEffect(() => {
    let cancelled = false;
    const loadInspection = async () => {
      if (!id) {
        setErrorMessage(t('inspection_not_found'));
        return;
      }
      if (isSavedInspectionLoading) {
        return;
      }
      const hydrationErr = (
        location.state as { openInspectionHydrationError?: 'offline_lite' | 'fetch_failed' } | null
      )?.openInspectionHydrationError;
      if (hydrationErr) {
        store.dispatch(clearOpenInspectionLoadError());
        setErrorMessage(
          t(
            hydrationErr === 'offline_lite'
              ? 'inspection_hydration_requires_connection'
              : 'inspection_start_failed'
          )
        );
        setInspectionDetails(null);
        setIsLoading(false);
        navigate(ROUTES.INSPECTION_START.replace(':id', id), { replace: true, state: {} });
        return;
      }
      setIsLoading(true);
      setErrorMessage(null);
      store.dispatch(clearOpenInspectionLoadError());
      try {
        const result = await loadInspectionForInspectApp({
          inspectionId: id,
          isOnline,
          savedInspection: savedInspection ?? null,
          listRow: cachedInspection,
          dispatch: store.dispatch,
        });
        if (cancelled) {
          return;
        }
        if (!result.ok) {
          if (result.reason === 'offline_lite') {
            setErrorMessage(t('inspection_hydration_requires_connection'));
          } else if (result.reason === 'not_found') {
            setErrorMessage(t('inspection_not_found'));
          } else {
            setErrorMessage(t('inspection_start_failed'));
          }
          setInspectionDetails(null);
          return;
        }
        setInspectionDetails(result.inspection);
      } catch (error) {
        if (cancelled) {
          return;
        }
        Sentry.captureException(error);
        setErrorMessage(t('inspection_start_failed'));
        setInspectionDetails(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void loadInspection();
    return () => {
      cancelled = true;
    };
  }, [
    id,
    isOnline,
    isSavedInspectionLoading,
    savedInspection,
    cachedInspection?.id,
    cachedInspection?.isFullDetailPreloaded,
    cachedInspection?.inspectionStatusId,
    location.state,
    navigate,
    t,
  ]);

  useEffect(() => {
    autoOpenHandledRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!id || !inspectionDetails || !user?.id || isLoading) {
      return;
    }
    if (isAdministrator) {
      return;
    }
    if (openInspection?.id && openInspection.id !== id) {
      return;
    }
    const decision = getInspectionStartPageDecision({
      inspection: inspectionDetails,
      inspectionId: id,
      currentUserId: user.id,
      startInspectionCheck: Boolean(startInspectionCheck),
    });
    if (decision.action === 'block') {
      const message = t(decision.messageKey);
      setErrorMessage((current) => (current === message ? current : message));
      return;
    }
    if (decision.action === 'open' && !autoOpenHandledRef.current) {
      autoOpenHandledRef.current = true;
      gotoInspection(inspectionDetails, id);
    }
  }, [
    id,
    inspectionDetails,
    user?.id,
    isLoading,
    isAdministrator,
    openInspection?.id,
    startInspectionCheck,
    gotoInspection,
    t,
  ]);

  const startInspection = async (): Promise<boolean> => {
    if (isSavedInspectionLoading) {
      return false;
    }
    if (!id || !inspectionDetails) {
      return false;
    }
    const isAdministratorRole = hasRole(ROLE.ADMINISTRATOR);
    const { body, startTime, shouldPut } = buildInspectionStartPut({
      inspection: inspectionDetails,
      userId: user?.id,
      isAdministrator: isAdministratorRole,
      deviceCoords: gpsVerification.getCoordsForInspectionUpdate(),
    });
    const inspectionWithStartTime: Inspection = startTime
      ? { ...inspectionDetails, startTime }
      : inspectionDetails;
    if (!isOnline || savedInspection) {
      gotoInspection(inspectionWithStartTime, id);
      return true;
    }
    if (isLoading) {
      return false;
    }
    setErrorMessage(null);
    let updatedInspection: Inspection | null = null;
    try {
      if (shouldPut) {
        const updateResponse = await clivApiRequest<Inspection>({
          url: inspectionUrl,
          method: 'PUT',
          body,
        });
        if (!updateResponse.ok) {
          setErrorMessage(t('inspection_start_failed'));
          return false;
        }
        updatedInspection = {
          ...inspectionWithStartTime,
          ...(body.inspectionStatusId ? { inspectionStatusId: body.inspectionStatusId } : {}),
          ...(body.inspectedBy ? { inspectedBy: body.inspectedBy } : {}),
        };
      }
      gotoInspection(updatedInspection ?? inspectionWithStartTime, id);
      return true;
    } catch (error) {
      Sentry.captureException(error);
      if (updatedInspection) {
        gotoInspection(updatedInspection, id);
        return true;
      }
      setErrorMessage(t('inspection_start_failed'));
      return false;
    }
  };

  const beginStartAction = () => {
    startInFlightRef.current = true;
    flushSync(() => {
      setIsStarting(true);
    });
  };

  const endStartAction = (keepLoading = false) => {
    if (keepLoading) {
      return;
    }
    startInFlightRef.current = false;
    setIsStarting(false);
  };

  const handleStart = async (event: MouseEvent<HTMLButtonElement>) => {
    if (startInFlightRef.current || isLoading || isStarting) {
      return;
    }
    if (openInspection && openInspection.id && openInspection.id !== id) {
      setShowOpenInspectionDialog(true);
      return;
    }
    event.currentTarget.disabled = true;
    beginStartAction();
    let keepLoading = false;
    try {
      const resolution = await gpsVerification.resolveStartWithGps();
      if (resolution.action === 'navigate') {
        navigate(resolution.path);
        keepLoading = true;
        return;
      }
      if (resolution.action === 'startWithReusedEvidence') {
        if (!id || !inspectionDetails) {
          return;
        }
        setErrorMessage(null);
        try {
          const started = await applyAutoReusedEvidenceAndStart({
            inspection: inspectionDetails,
            inspectionId: id,
            session: resolution.session,
            userId: user?.id,
            tenantId: systemConfig.tenantId,
            isOnline,
            isAdministrator: hasRole(ROLE.ADMINISTRATOR),
            navigate,
          });
          if (!started) {
            setErrorMessage(t('inspection_start_failed'));
          }
          keepLoading = started;
        } catch (error) {
          Sentry.captureException(error);
          setErrorMessage(t('inspection_start_failed'));
        }
        return;
      }
      keepLoading = await startInspection();
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(t('inspection_start_failed'));
    } finally {
      endStartAction(keepLoading);
    }
  };

  const handleResumeExistingInspection = () => {
    conflictHandledRef.current = true;
    setShowOpenInspectionDialog(false);
    if (openInspection?.id) {
      navigate(ROUTES.INSPECTION_DETAILS.replace(':id', openInspection.id), {
        state: { skipStartWorkFlow: true },
        replace: true,
      });
    }
  };

  const handleConfirmDiscardExistingInspection = async () => {
    if (startInFlightRef.current || isStarting) {
      return;
    }
    conflictHandledRef.current = true;
    if (openInspection) {
      store.dispatch(closeInspection(openInspection.id));
    }
    setShowOpenInspectionDialog(false);
    beginStartAction();
    let keepLoading = false;
    try {
      keepLoading = await startInspection();
    } finally {
      endStartAction(keepLoading);
    }
  };

  const handleGoBack = () => {
    navigate(ROUTES.INSPECTIONS, { replace: true });
  };

  if (!id) {
    navigate(ROUTES.INSPECTIONS);
    return null;
  }

  const showError = Boolean(errorMessage);
  const messageText = isLoading
    ? t('loading_inspection_details')
    : t('start_inspection_confirm_message');
  const showInitialLoader =
    isSavedInspectionLoading ||
    isLoading ||
    (gpsVerification.isEnabled && gpsVerification.isRequestingLocation);
  const isStartActionPending = isStarting;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        {!showError && (
          <>
            <h1 className={styles.title}>{t('start_inspection_confirm_title')}</h1>
            <p className={styles.message}>{messageText}</p>
          </>
        )}
        {showError && (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}
        <StartInspectionGpsVerification
          isEnabled={gpsVerification.isEnabled}
          locationMessageKey={gpsVerification.locationMessageKey}
          locationMessageClassName={styles.locationMessage}
        />
        {inspectionDetails && (
          <div className={styles.details}>
            <p className={styles.detailItem}>
              <strong>Product:</strong>{' '}
              {inspectionDetails.product
                ? `${inspectionDetails.product.name} (${inspectionDetails.product.code})`
                : '-'}
            </p>
            <p className={styles.detailItem}>
              <strong>Internal order ref:</strong> {inspectionDetails.internalOrderReference}
            </p>
            {inspectionDetails.externalOrderReference && (
              <p className={styles.detailItem}>
                <strong>External order ref:</strong> {inspectionDetails.externalOrderReference}
              </p>
            )}
          </div>
        )}
        {showInitialLoader && (
          <div className={styles.loaderContainer}>
            <CircularProgress size={24} />
          </div>
        )}
        {!isLoading && !isSavedInspectionLoading && (
          <div className={actionStyles.actions}>
            <button
              type="button"
              onClick={handleGoBack}
              className={actionStyles.cancelButton}
              disabled={isStartActionPending}
            >
              {t('Back')}
            </button>
            {!showError && (
              <>
                {gpsVerification.isLocationGateActive &&
                  Boolean(gpsVerification.locationMessageKey) && (
                    <button
                      type="button"
                      onClick={gpsVerification.retryLocation}
                      className={actionStyles.startButton}
                      disabled={gpsVerification.isRequestingLocation}
                    >
                      {gpsVerification.isRequestingLocation ? t('Loading') : t('retry')}
                    </button>
                  )}
                {!gpsVerification.isLocationGateActive && (
                  <button
                    type="button"
                    onClick={(event) => void handleStart(event)}
                    className={`${actionStyles.startButton} ${isStartActionPending ? actionStyles.startButtonLoading : ''}`.trim()}
                    disabled={isStartActionPending || !gpsVerification.canStart}
                    aria-busy={isStartActionPending}
                    aria-live="polite"
                  >
                    <span className={actionStyles.startButtonContent}>
                      {isStartActionPending && (
                        <CircularProgress size={18} color="inherit" aria-hidden="true" />
                      )}
                      <span>
                        {isStartActionPending
                          ? t('inspection_starting')
                          : t('start_inspection_confirm_button')}
                      </span>
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {showOpenInspectionDialog && openInspection && (
        <ExistingOpenInspectionDialog
          openInspection={openInspection}
          onResume={handleResumeExistingInspection}
          onDiscardConfirm={handleConfirmDiscardExistingInspection}
          onCancelDiscard={() => setShowOpenInspectionDialog(false)}
        />
      )}
    </div>
  );
};
